import { captureException } from "@sentry/nextjs";
import {
  action,
  computed,
  makeObservable,
  ObservableMap,
  observable,
} from "mobx";

import { fetchStatusForUser } from "@framework/notifications/graphql/fetch-status-for-user";
import { markNotificationsAsRead } from "@framework/notifications/graphql/mark-read";
import {
  AnyNotification,
  UserStore as UserStoreType,
  NotificationsStatus,
  DEFAULT_NOTIFICATIONS_STATUS,
  NotificationsStore as NotificationsStoreType,
} from "@voxmedia/duet-context/context/notifications-context";

const DEFAULT_STATUS_POLL_INTERVAL_MS = 5000;

class NotificationsStore implements NotificationsStoreType {
  public lookupById: ObservableMap<string, AnyNotification>;
  public userStore: UserStoreType;
  public notificationsStatus: NotificationsStatus;
  public pollIntervalId: NodeJS.Timeout | null;
  public originalDocumentTitle: string | undefined;

  public constructor(userStore: UserStoreType) {
    this.lookupById = observable.map(new Map<string, AnyNotification>());
    this.userStore = userStore;
    this.notificationsStatus = DEFAULT_NOTIFICATIONS_STATUS;
    this.pollIntervalId = null;

    makeObservable(this, {
      lookupById: observable,
      notificationsStatus: observable,
      markRead: action,
      setNotificationsStatus: action,
      insert: action,
      update: action,
      unseenNotificationsDisplayCount: computed,
    });

    if (typeof window !== "undefined") {
      this.originalDocumentTitle = window.document.title;
    }
  }

  public get unseenNotificationsDisplayCount() {
    if (
      !this.notificationsStatus.unseen ||
      typeof this.notificationsStatus.unseen !== "number"
    ) {
      return null;
    }

    const unseenCount = Math.max(this.notificationsStatus.unseen, 0); // sanity check
    if (!unseenCount) {
      return null;
    }

    return Math.min(unseenCount, 99);
  }

  get(id: string) {
    return this.lookupById.get(id);
  }

  insert(notification: AnyNotification) {
    const existing = this.lookupById.get(notification.id);
    if (existing) {
      this.update(existing, notification);
    } else {
      const observableNotification = observable.object(notification);
      this.lookupById.set(observableNotification.id, observableNotification);
    }

    return this.lookupById.get(notification.id);
  }

  update(existing: AnyNotification, notification: AnyNotification) {
    for (const key in notification) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      (existing as any)[key] = (notification as any)[key];
    }

    return existing;
  }

  public async markRead(params: {
    ids?: string[];
    all?: boolean;
    updateStore?: boolean;
  }) {
    if (!this.userStore.viewer?.token) {
      return;
    }

    await markNotificationsAsRead({
      ...params,
      token: this.userStore.viewer.token,
    });

    const { updateStore = true } = params;
    if (!updateStore) {
      return;
    }

    Array.from(this.lookupById.values()).map((notification) => {
      if (params.ids?.includes(notification.id) || params.all) {
        this.update(notification, { ...notification, read: true });
      }
    });
  }

  public setNotificationsStatus(notificationsStatus: NotificationsStatus) {
    this.notificationsStatus = notificationsStatus;
  }

  public startPollingNotificationsStatus(
    intervalMs = DEFAULT_STATUS_POLL_INTERVAL_MS,
  ) {
    if (this.pollIntervalId) {
      return;
    }

    // start immediately
    void this.pollNotificationsStatus();

    // set up interval
    this.pollIntervalId = setInterval(() => {
      void this.pollNotificationsStatus();
    }, intervalMs);
  }

  public stopPollingNotificationsStatus() {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  }

  private async pollNotificationsStatus() {
    if (!this.userStore.viewer?.token) {
      return;
    }

    try {
      const notificationsStatus = await fetchStatusForUser({
        token: this.userStore.viewer.token,
      });

      this.setNotificationsStatus(notificationsStatus);

      if (typeof window !== "undefined" && this.originalDocumentTitle) {
        if (this.unseenNotificationsDisplayCount) {
          window.document.title = `(${this.unseenNotificationsDisplayCount}) ${this.originalDocumentTitle}`;
        } else {
          window.document.title = this.originalDocumentTitle;
        }
      }
    } catch (error) {
      captureException(error, {
        tags: {
          context: "notifications",
        },
        extra: {
          viewerId: this.userStore.viewer.id,
        },
      });

      // if we get an error, stop the polling
      this.stopPollingNotificationsStatus();
    }
  }
}

export default NotificationsStore;
